/**
 * Keeping the reader in place across a zoom (#660).
 *
 * Changing the scale rebuilds the document at a different size while the
 * scroll container keeps its offset, so the same `scrollTop` lands somewhere
 * else — in a multi-page document, on a different page. The fix is to note what
 * the reader was looking at before the change and to scroll back to it after.
 *
 * The anchor is a page plus a fraction of that page, not a proportion of the
 * whole document: the container's padding and the gaps between pages do not
 * scale with it, so a purely proportional offset drifts by one gap for every
 * page above the viewport.
 */

/** A page as laid out in the scroll container. */
export interface PageBox {
  top: number
  height: number
}

export interface ZoomAnchor {
  /** Index into the page list. */
  page: number
  /** How far into that page the anchored point sits, 0…1. */
  fraction: number
  /** Where the anchored point sat in the viewport, in pixels from its top. */
  offset: number
}

/**
 * What the reader is looking at, given where the viewport sits.
 *
 * `offset` is measured from the top of the viewport — the middle of it for a
 * toolbar button, the pointer for a wheel zoom. Returns null when the point
 * falls past the last page, which leaves the caller nothing to restore.
 */
export function captureAnchor(
  pages: PageBox[],
  scrollTop: number,
  offset: number,
): ZoomAnchor | null {
  const target = scrollTop + offset
  const page = pages.findIndex((p) => p.top + p.height > target)
  if (page < 0) return null
  const { top, height } = pages[page]
  return { page, fraction: height > 0 ? (target - top) / height : 0, offset }
}

/**
 * The scroll offset that puts the anchored point back under the same pixel.
 *
 * Returns null when the anchored page is gone — a document swapped underneath
 * the zoom has no position worth restoring.
 */
export function restoreScrollTop(pages: PageBox[], anchor: ZoomAnchor): number | null {
  const page = pages[anchor.page]
  if (!page) return null
  return Math.max(0, page.top + anchor.fraction * page.height - anchor.offset)
}
